import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import environment from "../../environments/environment";
import {
  PaginatedMigrationDetailedMetricsResponse,
  SiteByProtocolCompany,
} from "@app/interface/interfaces";
import { RejectMigrationRequest, RemigrateMigrationRequest } from "@app/interface/models";

@Injectable({ providedIn: "root" })
export class ApiService {
  apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /** GET companies  */
  getCompanies(): Observable<any> {
    return this.http.get<any>(this.apiUrl + "stage/iris_companies").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }
  /** GET company protocols  */
  getCompanyProtocols(company_id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "stage/protocols-to-migrate/" + company_id)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }
  /** GET migration exists for company  */
  getMigrationByCompany(company_id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/migration-status/" + company_id)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }
  /** Push company migration (queues it)  */
  public pushCompany(company_id: number): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + "stage/push-company/" + company_id, null)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** Remove a migration from the queue */
  dequeueMigration(migrationId: number): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + `stage/queued-migrations/${migrationId}`)
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** Remove all migrations from the queue */
  dequeueAllMigrations(): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + 'stage/queued-migrations')
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** Get all queued migrations */
  getQueuedMigrations(): Observable<any> {
    return this.http
      .get<any[]>(this.apiUrl + "stage/queued-migrations")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** Reject company migration  */
  public rejectCompanyMigration(
    company_id: number,
    data: RejectMigrationRequest,
  ): Observable<any> {
    return this.http
      .post<RejectMigrationRequest>(
        this.apiUrl + "stage/reject/" + company_id,
        data,
      )
      .pipe(
        map((res: RejectMigrationRequest) => res),
        catchError(this.handleError),
      );
  }

  /** Remigrate company migration */
  public remigrateCompanyMigration(
    migration_id: number,
    data: RemigrateMigrationRequest,
  ): Observable<any> {
    return this.http
      .post<any>(
        this.apiUrl + "stage/remigrate-company/" + migration_id,
        data,
      )
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** GET migrations  */
  getMigrations(options?: {
    startDatetime?: string;
    endDatetime?: string;
    ignoreColumns?: string[];
  }): Observable<any> {
    let params = new HttpParams();

    if (options?.startDatetime) {
      params = params.set("start_datetime", options.startDatetime);
    }

    if (options?.endDatetime) {
      params = params.set("end_datetime", options.endDatetime);
    }

    // Send repeated query params only when values are provided.
    // Empty array means no ignore_columns are sent.
    if (options?.ignoreColumns && options.ignoreColumns.length > 0) {
      for (const column of options.ignoreColumns) {
        params = params.append("ignore_columns", column);
      }
    }

    return this.http.get<any>(this.apiUrl + "migration/all", { params }).pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  /** GET migrations with pagination  */
  getMigrationsPaginated(
    currentPage: number = 1,
    pageSize: number = 10,
    orderBy: string = "id",
    ignoreColumns: string[] = ["statistics"],
  ): Observable<any> {
    let params = new HttpParams();
    params = params.set("current_page", currentPage);
    params = params.set("page_size", pageSize);
    params = params.set("order_by", orderBy);

    // Send repeated query params only when values are provided.
    if (ignoreColumns && ignoreColumns.length > 0) {
      for (const column of ignoreColumns) {
        params = params.append("ignore_columns", column);
      }
    }

    return this.http
      .get<any>(this.apiUrl + "migration/", { params })
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** GET deleted/rejected migrations */
  getDeletedMigrations(): Observable<any> {
    return this.http.get<any>(this.apiUrl + "migration/deleted").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  /** GET migration by id  */
  getMigrationDetails(id: number): Observable<any> {
    return this.http.get<any>(this.apiUrl + "migration/" + id + "/").pipe(
      map((res: any) => res),
      catchError(this.handleError),
    );
  }

  getDeletedMigrationDetails(id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/deleted/" + id + "/")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** GET migration by id  */
  getMigrationMetrics(id: number): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/" + id + "/metric/")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  getDetailedMigrationMetrics(
    id: number,
    currentPage: number = 1,
    pageSize: number = 10,
    sortBy: string = "id",
    sortDirection: string = "asc",
    entityType?: string,
    searchText?: string,
  ): Observable<PaginatedMigrationDetailedMetricsResponse> {
    const params: Record<string, string | number> = {
      current_page: currentPage,
      page_size: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };

    if (entityType) {
      params["entity_type"] = entityType;
    }

    if (searchText) {
      params["search_text"] = searchText;
    }

    return this.http
      .get<PaginatedMigrationDetailedMetricsResponse>(
        this.apiUrl + "migration/" + id + "/metric/details/",
        {
          params,
        },
      )
      .pipe(
        catchError(this.handleError),
      ) as Observable<PaginatedMigrationDetailedMetricsResponse>;
  }

  getFalloutRecords(
    companyId?: number,
    migrationId?: number,
  ): Observable<unknown[] | unknown> {
    if (!migrationId) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    const url = this.apiUrl + `stage/migration/${migrationId}/fallout-details`;

    return this.http.get<unknown[]>(url).pipe(
      map((res: unknown) => {
        if (!companyId || !Array.isArray(res)) {
          return res;
        }

        return res.filter(
          (record: unknown): record is { company_id: number } => {
            if (
              !record ||
              typeof record !== "object" ||
              !("company_id" in record)
            ) {
              return false;
            }

            return record.company_id === companyId;
          },
        );
      }),
      catchError(this.handleError),
    );
  }

  getPlaceHolders(): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + "migration/placeholder-values")
      .pipe(
        map((res: any) => res),
        catchError(this.handleError),
      );
  }

  /** Filter protocols and return those not yet migrated */
  filterNotMigratedProtocols(
    migration_id: number,
    protocol_ids: number[],
  ): Observable<number[]> {
    return this.http
      .post<number[]>(this.apiUrl + "migration/filter-not-migrated", {
        migration_id,
        protocol_ids,
      })
      .pipe(
        catchError(this.handleError),
      ) as Observable<number[]>;
  }

  /** GET sites by company and selected protocol ids */
  getSitesByProtocolsAndCompany(
    company_id: number,
    protocol_ids: number[],
  ): Observable<SiteByProtocolCompany[]> {
    return this.http
      .post<SiteByProtocolCompany[]>(this.apiUrl + "migration/sites", {
        company_id,
        protocol_ids,
      })
      .pipe(catchError(this.handleError)) as Observable<SiteByProtocolCompany[]>;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError = (error: HttpErrorResponse): any => {
    const newError: any = new Error(`${error}`);
    newError.details = error;

    return throwError(() => newError);
  };
}
